import type {
  AuthActivateUser,
  AuthPasswordChangeData,
} from '@robovision/rvai-auth'
import {
  distinct,
  HttpStatusCode,
  mergeArraysByKey,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import { Member } from '~domain/Member'
import { rvaiAuthApi } from '~services/api'
import {
  acceptStatus,
  retryIfUnauthorized,
} from '~utils/api'
import { useSessionStore } from './useSessionStore'


interface MemberCreationData {
  email: string
  roleId: string
}

export const useMembersStore = defineStore('members', () => {
  const state = reactive({
    members: [] as Member[],
  })

  const getters = {
    getMember (id: string): Member | undefined {
      return state.members.find(m => m.id === id)
    },
  }

  const actions = {
    async acceptTermsAndConditions () : Promise<void> {
      await rvaiAuthApi.users.acceptTermsOfService()
    },

    async acceptTermsAndConditionsWithLogin (username: string, password: string) : Promise<void> {
      await rvaiAuthApi.login.login({
        password,
        username,
      })

      // this retry mechanism is necessary to handle the scenario
      // where the auth service returns a valid response for the login
      // but the jwt token has not been stored into the DB yet,
      // which causes the subsequent calls to fail for a brief period.
      // TODO remove the retry once the issue is fixed in RVAI-9445
      await retryIfUnauthorized(() => rvaiAuthApi.users.acceptTermsOfService())
    },

    async activateUser (data: AuthActivateUser): Promise<void> {
      await rvaiAuthApi.users.activateUser(data)
    },

    async checkPassword (password: string): Promise<boolean> {
      const { currentUser } = useSessionStore()
      if (!currentUser) return false

      const response = await rvaiAuthApi.login.login(
        {
          password,
          username: currentUser.email,
        },
        { validateStatus: acceptStatus(HttpStatusCode.UNAUTHORIZED) },
      )

      return response?.status !== HttpStatusCode.UNAUTHORIZED
    },

    async deleteMembers (members: Member[]): Promise<void> {
      const {
        currentUser,
        logout,
      } = useSessionStore()

      if (!currentUser) return

      const membersToRemove = state.members.length === members.length
        ? members.filter(m => m.id !== currentUser.id)
        : members

      const ids = membersToRemove.map((m) => m.id)
      await Promise.all(ids.map(id => rvaiAuthApi.users.deleteUser(id)))

      state.members = state.members.filter((m) => !ids.includes(m.id))
      if (ids.includes(currentUser.id)) {
        await logout()
      }
    },

    async inviteUser (creationData: MemberCreationData): Promise<Member> {
      // firstName and lastName are required
      const { data: userData } = await rvaiAuthApi.users.inviteUser({
        email: creationData.email,
        firstName: creationData.email,
        lastName: creationData.email,
        roleId: creationData.roleId,
      })

      const createdMember = Member.fromResponse(userData)

      state.members = [createdMember, ...state.members].sort(Member.sort)
      return createdMember
    },

    async loadMemberByToken (token: string): Promise<Member> {
      const { data: userData } = await rvaiAuthApi.users.getInvitationTokenInfo({
        invitationToken: token,
      })
      return Member.fromResponse(userData)
    },

    async loadMembers ({
      emails,
      ids,
    }: {
      emails?: string[]
      ids?: string[]
    } = {}): Promise<void> {
      const { data } = await rvaiAuthApi.users.listUsers({
        filter: [
          ...distinct(emails ?? []).map(email => `email:${email}`),
          ...distinct(ids ?? []).map(id => `id:${id}`),
        ],
      })

      const members = data.items.map(Member.fromResponse)
      state.members = mergeArraysByKey(state.members, members, 'id').sort(Member.sort)
    },

    async regenerateMemberInvitationToken (id: string): Promise<Member> {
      const { data: userData } = await rvaiAuthApi.users.regenerateInviteToken(id)

      const member = Member.fromResponse(userData)

      state.members = state.members.map(m => m.id === member.id ? member : m)

      return member
    },

    async requestPasswordReset (email: string): Promise<void> {
      await rvaiAuthApi.users.requestPasswordReset({ email })
    },

    async setNewPassword (data: AuthPasswordChangeData): Promise<void> {
      await rvaiAuthApi.users.changePassword(data)
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async updateMember (userId: string, roleId: string): Promise<Member> {
      const { data: responseData } = await rvaiAuthApi.users.updateUserRole(userId, { roleId })
      const updatedMember = Member.fromResponse(responseData)
      state.members = state.members.map(m => m.id === userId ? updatedMember : m)
      return updatedMember
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...actions,
    ...getters,
  }
})
